import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { EventEmitter, Injectable, Output } from '@angular/core';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import {
  ObraModel,
  UserLoggedModel,
} from 'src/app/shared/models/UserLoginModel.model';
import { environment } from 'src/environments/environment';
import { TransportistaLoginGatewayApi } from 'src/core/apis/login';
import { TransportistaDatosUsuarioGatewayApi } from 'src/core/apis/datos-usuario';
import { TransportistaSessionStorage } from 'src/core/jwt/session.storage';
import { GetDataService } from './get-data.service';
import {
  buildLoginComponentPayload,
  extractGatewayJwt,
  mapGatewayDatosRowToMixerUserLogged,
} from './gateway-auth.mapper';
import {
  GatewayUsuarioRow,
} from './gateway-auth.types';
import { CacheWarmupService } from 'src/app/services/offline/cache-warmup.service';
import { OfflineStorageService } from 'src/app/services/offline/offline-storage.service';
import { OrderService } from 'src/app/services/pedidos/pedidos.service';
import { EventLogService } from 'src/app/shared/logging/event-log.service';
import { CorrelationContextService } from 'src/core/logging/correlation-context.service';
import { SessionIdService } from 'src/core/logging/session-id.service';
import { EVENT_LOG_ENDPOINTS } from 'src/core/logging/event-log-endpoints';
import {
  httpErrorContext,
  httpSuccessContext,
  mergeHttpLogPayload,
} from 'src/core/logging/http-event-log-context';
import { ExecutionContextService } from 'src/core/context/execution-context.service';

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  @Output() fireIsLoggedIn: EventEmitter<any> = new EventEmitter<any>();
  private loggedIn = new BehaviorSubject<boolean>(false);

  private userSubject: BehaviorSubject<UserLoggedModel>;
  public user: Observable<UserLoggedModel>;
  private userPassword: string;
  endpoint: string;
  endpointRecoveryPassword: string;

  constructor(
    private router: Router,
    private http: HttpClient,
    private GetDataSrvice: GetDataService,
    private readonly loginGatewayApi: TransportistaLoginGatewayApi,
    private readonly datosUsuarioGatewayApi: TransportistaDatosUsuarioGatewayApi,
    private readonly gatewaySession: TransportistaSessionStorage,
    private readonly cacheWarmup: CacheWarmupService,
    private readonly offlineStorage: OfflineStorageService,
    private readonly orderService: OrderService,
    private readonly eventLog: EventLogService,
    private readonly correlationContext: CorrelationContextService,
    private readonly sessionIdService: SessionIdService,
    private readonly executionContext: ExecutionContextService
  ) {
    this.endpoint = environment.ResourceLogin;
    this.endpointRecoveryPassword = environment.ResourcePassWordRecovery;
    this.userSubject = new BehaviorSubject<UserLoggedModel>(
      JSON.parse(localStorage.getItem('user'))
    );
    this.userPassword = localStorage.getItem('password');
    this.user = this.userSubject.asObservable();
    if (this.userSubject.value) {
      this.executionContext.setAuthenticatedUser(this.userSubject.value);
    }
  }

  public get userValue(): UserLoggedModel {
    return this.userSubject.value;
  }
  public get userClave(): string {
    return this.userPassword;
  }

  loginPolpaico(username, password) {
    if (environment.useGatewayAuth) {
      return this.loginPolpaicoGateway(username, password);
    }
    this.saveClave(password);
    return this.http
      .get<UserLoggedModel>(this.endpoint, {
        headers: { User: username, Pass: password },
      })
      .pipe(
        map((user) => {
          if (user['length'] === 0 || user['datos'] == undefined) {
            return user;
          }
          // store user details and jwt token in local storage to keep user logged in between page refreshes
          if (user['datos'].length === 0) {
            return user['datos'];
          }
          const data: any = {
            USUARIOERO:  user['datos'][0]['USUARIOERO'],
            PASSWORDERO:  user['datos'][0]['PASSWORDERO'],
            APIKEYERO:  user['datos'][0]['APIKEYERO'],
            RUT_EMPRESA: user['datos'][0]['RUT_M'],
            NOMBRE_EMPRESA: user['datos'][0]['NOMBRE_M'],
            NOMBRE_USUARIO: user['datos'][0]['NOMBRE_M'],
            RUT_USUARIO: user['datos'][0]['RUT_M'],
            MAIL_USUARIO: user['datos'][0]['EMAIL_M'],
            ADM: '0',
            FONO_USUARIO: user['datos'][0]['CELULAR_M'],
            KEY: user['datos'][0]['KEY'],
            OBRASNOTIFICACIONES: user['datos'][0]['OBRAS'],
            CARGO: 'MIXER',
            ROL: user['datos'][0]['ROL'],
            INTERNO: user['datos'][0]['INTERNO'],
            VIAJES_M: user['datos'][0]['VIAJESH'],
            OBRAS: [],
            ESTADO: user['datos'][0]['ESTADO'],
            firma: '',
            DIAS_DESDE_ULTIMA_ENCUESTA : user['datos'][0]['ROL'].toUpperCase() === 'MIXER' ? user['datos'][0]['DIAS_DESDE_ULTIMA_ENCUESTA'] : null,
            OPERADOR_DE_PLANTA : user['datos'][0]['OPERADOR_DE_PLANTA'],  
            PLANTA_M :  user['datos'][0]['PLANTA_M']

            };

          this.saveDatos(data);
          this.loggedIn.next(true);
          
          this.fireIsLoggedIn.emit('loggedIn');
          return user;
        })
      );
  }

  /**
   * POST /login + POST /datos-usuario (Bearer vía interceptor).
   * Swagger QAS: https://wsqa.polpaicoconexion.cl/swagger/index.html
   * - Body login: `{ user, pass }` y opcional `rutEmp` si el API lo soporta para CEMAR; si no, poner `useGatewayAuth: false` o ajustar contrato.
   * - `Estado` en el payload hacia LoginComponent sale de la respuesta de login/datos-usuario si el backend lo envía.
   */
  private loginPolpaicoGateway(
    username: string,
    password: string,
    rutEmp?: string
  ): Observable<any> {
    this.saveClave(password);
    const correlationId = this.correlationContext.startFlow();
    this.eventLog.log('auth', 'login_iniciado', { usuarioId: username, correlationId });
    const loginRequest = rutEmp
      ? { user: username, pass: password, rutEmp }
      : { user: username, pass: password };
    return this.loginGatewayApi.login(loginRequest).pipe(
        switchMap((loginResp) => {
          const token = extractGatewayJwt(loginResp);
          if (!token) {
            this.gatewaySession.clearToken();
            return throwError({ status: 0, message: 'Login sin token JWT.' });
          }
          this.gatewaySession.setToken(token);
          return this.datosUsuarioGatewayApi
            .fetch()
            .pipe(map((datosResp) => ({ loginResp, datosResp })));
        }),
        map(({ loginResp, datosResp }) => {
          const datos = datosResp?.datos;
          if (!datos || datos.length === 0) {
            this.gatewaySession.clearToken();
            return [];
          }
          const row = datos[0] as GatewayUsuarioRow;
          this.gatewaySession.setUser(row);
          if (rutEmp) {
            const data: any = { ...row };
            data['OBRAS'] = this.limpiarDataObra(data['OBRAS'] || []);
            this.saveDatos(data);
            this.fireIsLoggedIn.emit('loggedIn');
          } else {
            const data = mapGatewayDatosRowToMixerUserLogged(row) as any;
            this.saveDatos(data);
            this.loggedIn.next(true);
            this.fireIsLoggedIn.emit('loggedIn');
          }
          this.sessionIdService.createSessionId();
          this.eventLog.log(
            'auth',
            'login_ok',
            mergeHttpLogPayload(
              { usuarioId: username, correlationId },
              httpSuccessContext(EVENT_LOG_ENDPOINTS.AUTH_LOGIN)
            )
          );
          return buildLoginComponentPayload(loginResp, datosResp, datos);
        }),
        catchError((err) => {
          this.gatewaySession.clearAll();
          const httpCtx =
            err instanceof HttpErrorResponse
              ? httpErrorContext(err, EVENT_LOG_ENDPOINTS.AUTH_LOGIN)
              : {
                  endpointOrigen: EVENT_LOG_ENDPOINTS.AUTH_LOGIN,
                  message: err?.message ? String(err.message) : undefined,
                };
          this.eventLog.log(
            'auth',
            'login_error',
            mergeHttpLogPayload({ usuarioId: username, correlationId }, httpCtx)
          );
          return throwError(err);
        }),
        finalize(() => this.correlationContext.endFlow())
      );
  }

  loginLocal(user: UserLoggedModel, password: string) {
    this.saveClave(password || '');
    this.saveDatos(user);
    this.loggedIn.next(true);
    this.fireIsLoggedIn.emit('loggedIn');
  }
  loginConRutEmpresaPolpaico(username, password, RutEmp) {
    if (environment.useGatewayAuth) {
      return this.loginPolpaicoGateway(username, password, RutEmp);
    }
    this.saveClave(password);
    return this.http
      .get<UserLoggedModel>(this.endpoint, {
        headers: { User: username, Pass: password, RutEmp: RutEmp },
      })
      .pipe(
        map((user) => {
          if (user['length'] === 0 || user['datos'] === undefined) {
            return user;
          }
          // store user details and jwt token in local storage to keep user logged in between page refreshes
          if (user['datos'].length === 0) {
            return user['datos'];
          }
          let data: any = user['datos'][0];
          data['OBRAS'] = this.limpiarDataObra(data['OBRAS']);
          this.saveDatos(data);
          this.fireIsLoggedIn.emit('loggedIn');
          return user;
        })
      );
  }
  saveDatos(datos: UserLoggedModel) {
    localStorage.setItem('user', JSON.stringify(datos));
    this.executionContext.setAuthenticatedUser(datos);

    this.RecardarDataService();
    let today = moment(); //se agrega la expiracion del token
    //let tomorrow = moment(today).add(3, 'days');
    let tomorrow = moment(today).add(8, 'hours');
    localStorage.setItem('TimeExpire', JSON.stringify(tomorrow));
    this.setCookie(datos.RUT_USUARIO);

    
    this.userSubject.next(datos);
  }
  saveClave(clave: string) {
    localStorage.setItem('password', clave);
    this.userPassword = localStorage.getItem('password');
  }
  recoverPassword(rutUser, rutEmp) {
    return this.http.get<string>(this.endpointRecoveryPassword, {
      headers: { RutUser: rutUser, RutEmp: rutEmp },
    });
  }
  private limpiarDataObra(listObras) {
    let obraLimpias: ObraModel[] = listObras.reduce(
      (acc: ObraModel[], item) => {
        let element: ObraModel[] = acc.filter(
          (x) => x.OB_CODIGO == item.OB_CODIGO
        );
        if (element.length == 0) {
          acc.push(item);
        }
        return acc;
      },
      []
    );
    return obraLimpias.sort(function (a, b) {
      return a.OB_NOMBRE > b.OB_NOMBRE ? 1 : a.OB_NOMBRE < b.OB_NOMBRE ? -1 : 0;
    });
  }
  getEmitter() {
    return this.fireIsLoggedIn;
  }

  logout() {
    const usuarioId = this.userValue?.RUT_USUARIO
      ? String(this.userValue.RUT_USUARIO)
      : undefined;
    this.eventLog.log('auth', 'logout', { usuarioId });
    this.sessionIdService.clearSessionId();
    this.correlationContext.endFlow();
    this.cacheWarmup.cancelPending();
    this.orderService.clearRecentMutations();
    this.offlineStorage.clearAllCache().catch(() => null);
    this.cacheWarmup.resetSession();
    this.gatewaySession.clearAll();
    this.executionContext.clearAuthenticated();
    this.RemoveDataStore();
    localStorage.removeItem('user');
    localStorage.removeItem('TimeExpire');
    sessionStorage.removeItem('modalInfoShow');
    this.deleteCookie('Rut');
    this.userSubject.next(null);
    this.loggedIn.next(false);
    this.router.navigate(['/login']);
  }

  isVigenteKey() {
    if (environment.useGatewayAuth) {
      if (!this.gatewaySession.getToken() || this.gatewaySession.isTokenExpired()) {
        return false;
      }
    }
    let TimeCreacion = new BehaviorSubject<moment.Moment>(
      JSON.parse(localStorage.getItem('TimeExpire'))
    );
    let today = moment();
    if (today > moment(TimeCreacion.value)) {
      return false;
    } else {
      return true;
    }
  }

  async SaveDataServices(_dataUser: UserLoggedModel) {
    // Sin pantalla de historial de programación: no se precarga pedidos_h.
  }

  RemoveDataStore() {
    localStorage.removeItem('password');
    localStorage.removeItem('TimeExpireSaldos');
  }

  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async RecardarDataService() {
    await this.sleep(10800000);

    this.RemoveDataStore();
    let UserStora = new BehaviorSubject<UserLoggedModel>(
      JSON.parse(localStorage.getItem('user'))
    );
    this.SaveDataServices(UserStora.value);
  }
  setCookie(rut) {
    const cookieName = 'Rut';
    const cookieValue = rut;
    const expirationDays = 3;

    const date = new Date();
    date.setTime(date.getTime() + expirationDays * 24 * 60 * 60 * 1000);
    const expires = 'expires=' + date.toUTCString();

    document.cookie =
      cookieName + '=' + cookieValue + ';' + expires + ';path=/';
  }

  deleteCookie(nameCookie) {
    const expirationDate = new Date('2000-01-01');
    document.cookie =
      nameCookie + '=; expires=' + expirationDate.toUTCString() + '; path=/';
  }

  updateStatus(status: string) {
    if (this.userSubject.value) {
      let user = this.userSubject.value;
      user.ESTADO = status;
      this.userSubject.next(user);
      this.saveDatos(user);
    }
  }

  get isLoggedIn() {
    return this.loggedIn.asObservable();
  }

  getChatPolpaico() {
    if (this.userSubject.value) {
      return this.http
      .get<UserLoggedModel>(this.endpoint, {
        headers: { User: this.userSubject.value.RUT_USUARIO, Pass: this.userPassword, SOLO_CHAT:'1' },
      });
    }
    
  }
}
